import { Checkbox, Chip, Grid, ListItem, ListItemIcon, ListItemProps, ListItemText, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { EnhancedPhoneNumber, PhoneNumber, PhoneNumberType } from "../../../../Models/PhoneNumber";
import ClearableTextField from "../../../Helpers/ClearableTextField";
import SelectList, { CustomListItem } from "../../../Helpers/SelectList";
import ToolWrapper from "../ToolWrapper";
import PhoneNumberSettingsEditForm from "./PhoneNumberSettings.EditForm";
import { PhoneNumberSettingsReadme } from "./PhoneNumberSettings.Readme";
import useGetPhoneNumbers from "./PhoneNumberSettings.UseGetPhoneNumbers";
import useUpsertPhoneNumbers from "./PhoneNumberSettings.UsePutPhoneNumbers";

interface PhoneNumberListItem extends EnhancedPhoneNumber {
  show: boolean;
}

const PhoneNumberSettings: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState<EnhancedPhoneNumber>();
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberListItem[]>();
  const [filter, setFilter] = useState("");

  const { res: getResponse } = useGetPhoneNumbers();
  useEffect(
    () => getResponse && setPhoneNumbers(getResponse.payload.map(p => ({ ...new EnhancedPhoneNumber(p), show: true }))),
    [getResponse]
  );

  const { res: upsertResponse, loading: upsertLoading, setRequest } = useUpsertPhoneNumbers();
  useEffect(() => {
    if (!upsertResponse) return;
    setPhoneNumbers(oldNumbers => {
      if (!oldNumbers) return oldNumbers;
      const updateIndex = oldNumbers.findIndex(n => n.id === upsertResponse.id);
      if (updateIndex !== -1) {
        oldNumbers[updateIndex] = { ...upsertResponse, show: true };
        toast.success("Updated the Phone Number");
      } else {
        oldNumbers.push({ ...upsertResponse, show: true });
        toast.success("Created the Phone Number");
      }
      return [...oldNumbers];
    });
    setPhoneNumber(new EnhancedPhoneNumber(upsertResponse));
  }, [upsertResponse]);
  const onChangeFilter = (value: string) => {
    setPhoneNumbers(numbers =>
      numbers?.map(p => ({
        ...p,
        show: !value || p.value.includes(value) || p.note?.toLowerCase().includes(value.toLowerCase()) || false
      }))
    );
    setFilter(value);
  };

  return (
    <ToolWrapper title="Phone Number Settings" readme={PhoneNumberSettingsReadme.tool} isLoading={!phoneNumbers}>
      <Grid container spacing={4} wrap="nowrap" sx={{ width: "100%", height: "100%", p: 2 }}>
        <Grid item container direction="column" spacing={2} wrap="nowrap" sx={{ height: "100%" }}>
          <Grid item>
            <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="h6">Phone Numbers:</Typography>
              </Grid>
              <Grid item>
                <ClearableTextField
                  label="Filter"
                  value={filter}
                  onChange={({ target: { value } }) => onChangeFilter(value)}
                  onClear={() => onChangeFilter("")}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs sx={{ px: 0.5, overflowY: "auto", overflowX: "hidden" }}>
            <SelectList
              isValueSelected={option => option.id === phoneNumber?.id}
              items={phoneNumbers?.filter(p => p.show) || []}
              disabled={upsertLoading}
              onChange={numbers => setPhoneNumber(numbers[0])}
              renderListItem={props => <PhoneNumberItem {...props} />}
            />
          </Grid>
        </Grid>
        <Grid item>
          <PhoneNumberSettingsEditForm
            isLoading={upsertLoading}
            phoneNumber={phoneNumber}
            phoneNumbers={phoneNumbers || []}
            setPhoneNumber={setPhoneNumber}
            setUpdate={setRequest}
          />
        </Grid>
      </Grid>
    </ToolWrapper>
  );
};

const PhoneNumberItem: React.FC<CustomListItem<PhoneNumber>> = ({ data: phoneNumber, selected, ...rest }) => {
  delete rest["disableRipple"];
  delete rest["hideCheckBoxes"];
  return (
    <ListItem {...(rest as unknown as ListItemProps)} sx={{ minWidth: "28rem" }}>
      <ListItemIcon>
        <Checkbox edge="start" checked={selected} tabIndex={-1} />
      </ListItemIcon>
      <ListItemText primary={EnhancedPhoneNumber.formatValue(phoneNumber.value)} />
      {phoneNumber.type !== PhoneNumberType["No Action"] && (
        <ListItemIcon>
          <Chip size="small" label={PhoneNumberType[phoneNumber.type]} />
        </ListItemIcon>
      )}
    </ListItem>
  );
};

export default PhoneNumberSettings;
